<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('audit.title') }}
        </h6>
      </div>

      <Menu/>

      <Search
          :filters="filters"
          :loading="isSearching"
          :officeDropdown="officeDropdown"
          :reportStatusDropdown="reportStatusDropdown"
          :officeEngagementTypesDropdown="officeEngagementTypesDropdown"
          @search="getAuditOfficeList"/>

      <div class="element-box mt-0">

        <div class="mb-3">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getAuditOfficeList"/>

          <ExcelExportButton
              style="margin-right: 15px;"
              :label="$t('general.excel_export')"
              :loading="isExcelLoaded"
              @click="getExcelExport"/>
        </div>

        <EmptyState
            v-if="!auditOfficeList.data?.length"
            icon="fa-building"
            title="Keine Standorte gefunden"
            subtitle="Es wurden keine Standorte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-2">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click="sortBy('engagement_type')">
                  {{ $t('general.office_engagement_type') }}
                  <i :class="getIconClass('engagement_type')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('name')">
                  <span>{{ $t('general.office_name') }}</span>
                  <i :class="getIconClass('name')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('latest_audit')" style="min-width: 200px;">
                  <span>Letzter Audit</span>
                  <i :class="getIconClass('latest_audit')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('next_audit')" style="min-width: 200px;">
                  Nächster udit
                  <i :class="getIconClass('next_audit')"></i>
                </a>
              </th>
              <th>
                <span>{{ $t('general.status') }}</span>
              </th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(data) in auditOfficeList.data" :key="data.office.id">
              <td>
                {{ data?.office?.engagementTypeTranslation }}
              </td>
              <td>
                {{ data?.office?.name }}
              </td>
              <td>
                <div v-if="data?.office?.latestReportDate !== null">
                  {{ data?.office?.latestReportDate }}
                </div>
              </td>
              <td>
                <div v-if="data?.nextReport !== null">
                  {{ data.nextReport.date }} &nbsp;&nbsp;
                  {{ data.nextReport.startTime }} -
                  {{ data.nextReport.endTime }}
                </div>
              </td>
              <td>
                <div :class="['status-pill', statusClass(data.status), 'p-2', 'mr-2']" data-toggle="tooltip"></div>
                <span>{{ statusText(data.status) }}</span>
              </td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'auditList', params: {officeId: data.office?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="auditOfficeList?.pagination?.total_pages > 1"
            :totalPages="auditOfficeList?.pagination?.total_pages"
            :currentPage="auditOfficeList?.pagination?.current_page"
            :totalItems="auditOfficeList?.pagination?.total_items"
            :perPage="auditOfficeList?.pagination?.items_per_page"
            @pagechanged="getAuditOfficeList"/>
      </div>
    </div>
  </PageTemplate>
</template>


<script setup>
import officeAuditService from '@/api/OfficeAudit';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import router from '@/router';
import {getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Menu from '@/views/App/OfficeAudit/Menu.vue';
import Search from '@/views/App/OfficeAudit/Audit/Search.vue';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const auditOfficeList = ref([]);
const isExcelLoaded = ref(false);

const filters = ref({
  officeId: '',
  engagementType: '',
  reportStatus: '',
  orderBy: 'DESC',
  sortBy: '',
});

const isSearching = ref(false);
const isLoaded = ref(false);
const officeDropdown = ref([]);
const officeEngagementTypesDropdown = ref([]);
const reportStatusDropdown = ref([]);

async function getAuditOfficeList(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = auditOfficeList.value?.pagination?.items_per_page;

  try {
    const {data} = await officeAuditService.getOfficeList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    auditOfficeList.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function getIconClass(key) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getAuditOfficeList();
}

function statusClass(status) {
  switch (status) {
    case 'red':
      return 'red';
    case 'orange':
      return 'yellow';
    case 'green':
      return 'green';
    default:
      return '';
  }
}

function statusText(status) {
  switch (status) {
    case 'red':
      return 'Mehr als 9 Monate';
    case 'orange':
      return 'Mehr als 6 Monate';
    case 'green':
      return 'Weniger als 6 Monate';
    default:
      return '';
  }
}

async function getOfficeEnumDropdowns() {
  officeEngagementTypesDropdown.value = getEnums.value?.officeEngagementTypes || [];
}

async function getReportStatusDropdown() {
  const optionsArray = [
    {
      'label': 'Grün',
      'value': 'green',
    },
    {
      'label': 'Gelb',
      'value': 'green',
    },
    {
      'label': 'Rot',
      'value': 'red',
    },
  ];

  reportStatusDropdown.value = optionsArray.map(item => ({
    label: `${item.label}`,
    value: item?.value,
  }));
}

async function getExcelExport() {
  await downloadFile('/office-audit/office-list/excel-export', undefined,
      filters,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getOfficeEnumDropdowns(),
    getOfficeDropdown(officeDropdown, t),
    getReportStatusDropdown(),
    getAuditOfficeList(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
</style>